/**
 * WriteFlow App Component
 * Main application component with routing and layout structure.
 */

import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthenticationContext';
import TopNavigationBar from './components/layout/TopNavigationBar';
import LeftSidebarNavigation from './components/layout/LeftSidebarNavigation';
import RightSidebarWidgets from './components/layout/RightSidebarWidgets';
import HomePageWithDailyPrompt from './pages/HomePageWithDailyPrompt';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import UserProfilePage from './pages/UserProfilePage';
import FeedPageWithPosts from './pages/FeedPageWithPosts';
import PostDetailPage from './pages/PostDetailPage';
import NotificationsPage from './pages/NotificationsPage';
import SavedPostsPage from './pages/SavedPostsPage';
import SettingsPage from './pages/SettingsPage';
import TagPage from './pages/TagPage';
import DayArchivePage from './pages/DayArchivePage';
import UsernameRedirectPage from './pages/UsernameRedirectPage';
import SearchUsersPage from './pages/SearchUsersPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import ContestPage from './pages/ContestPage';
import WritingStatsPage from './pages/WritingStatsPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import ReadPiecePage from './pages/ReadPiecePage';
import './App.css';

function AppLayout() {
  // The admin dashboard is a wide, data-dense page — drop the right sidebar (and the
  // margin it reserves) there so the tables get the full width.
  const isAdmin = useLocation().pathname === '/admin';
  return (
    <div className="App">
      <TopNavigationBar />
      <LeftSidebarNavigation />
      {!isAdmin && <RightSidebarWidgets />}
      <div className={`main-content${isAdmin ? ' main-content--full' : ''}`}>
        <Routes>
              <Route path="/" element={<HomePageWithDailyPrompt />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/feed" element={<FeedPageWithPosts />} />
              <Route path="/contest" element={<ContestPage />} />
              <Route path="/terms" element={<TermsOfServicePage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/explore" element={<Navigate to="/feed" replace />} />
              <Route path="/post/:id" element={<PostDetailPage />} />
              <Route path="/notifications" element={<NotificationsPage />} />
              <Route path="/saved" element={<SavedPostsPage />} />
              <Route path="/stats" element={<WritingStatsPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/tag/:tag" element={<TagPage />} />
              <Route path="/day/:n" element={<DayArchivePage />} />
              <Route path="/read/:postId" element={<ReadPiecePage />} />
              <Route path="/u/:username" element={<UsernameRedirectPage />} />
              <Route
                path="/profile/:id"
                element={<UserProfilePage />}
              />
              <Route path="/search" element={<SearchUsersPage />} />
              <Route path="/admin" element={<AdminDashboardPage />} />
              <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </div>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppLayout />
      </Router>
    </AuthProvider>
  );
}

export default App;
